import React, { useState, useRef, useEffect } from 'react';

// Custom SVG Icons
const PlayIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const Volume2Icon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
  </svg>
);

const VolumeXIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9" x2="17" y2="15"/>
    <line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);

const ReelPlayer = ({ 
  videoSrc, 
  thumbnailSrc,
  width = 270, 
  height = 483 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  // Handle video play/pause on hover
  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isHovered]);

  // Update progress
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  // Handle video end
  const handleVideoEnd = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setProgress(0);
      if (isHovered) {
        videoRef.current.play(); // Loop if still hovered
      }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div 
      className="relative bg-black rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer"
      style={{ width, height }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-100 h-100 object-cover"
        muted={isMuted}
        loop
        playsInline
        poster={thumbnailSrc}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleVideoEnd}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-600 bg-opacity-50">
        <div 
          className="h-full bg-white transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Play Button Overlay (when not hovered) */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
          isHovered ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="bg-black bg-opacity-60 rounded-full p-4 backdrop-blur-sm">
          <PlayIcon />
        </div>
      </div>

      {/* Mute/Unmute Button - Simple and Clean */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={toggleMute}
          className="p-3 bg-black bg-opacity-60 text-white hover:bg-opacity-80 rounded-full transition-all duration-200 backdrop-blur-sm"
        >
          {isMuted ? (
            <VolumeXIcon />
          ) : (
            <Volume2Icon />
          )}
        </button>
      </div>

      {/* Playing Indicator */}
      {isPlaying && (
        <div className="absolute top-4 right-4 z-20">
          <div className="bg-green-500 bg-opacity-90 px-2 py-1 rounded text-xs font-medium text-white">
            Playing
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelPlayer;